/**
 * Configuration des webhooks et journal des livraisons.
 *
 * Le point de terminaison est propre à cette plateforme : un webhook de
 * bac à sable ne reçoit jamais d'événement de production, et réciproquement.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, type ConfigWebhook, type Livraison } from '../../api/client';
import {
  Bandeau,
  Carte,
  Chargement,
  EtatVide,
  MessageErreur,
  MessageSucces,
  PuceLibre,
} from '../../components/Ui';
import { formaterDate } from '../../lib/format';
import { useEstProduction } from '../../lib/plateforme';

const EVENEMENTS = [
  ['message.sent', 'Le message est parti sur le réseau'],
  ['message.delivered', 'Remis au terminal'],
  ['message.undelivered', 'Non remis — le message a été recrédité'],
  ['message.expired', 'Expiré chez l’opérateur — recrédité'],
  ['message.rejected', 'Refusé avant émission — jamais débité'],
  ['message.opted_out', 'Filtré par la liste STOP — jamais facturé'],
];

/** `[60, 600, …]` → `1 min, 10 min, 1 h…`. */
function formaterDelai(secondes: number): string {
  if (secondes < 3_600) return `${Math.round(secondes / 60)} min`;
  return `${Math.round(secondes / 3_600)} h`;
}

const ETAT_LIVRAISON = {
  delivered: { ton: 'succes' as const, libelle: 'Livré' },
  pending: { ton: 'attente' as const, libelle: 'En attente' },
  failed: { ton: 'echec' as const, libelle: 'Abandonné' },
};

export function Webhooks() {
  const estProduction = useEstProduction();
  const [config, setConfig] = useState<ConfigWebhook | null>(null);
  const [livraisons, setLivraisons] = useState<Livraison[] | null>(null);
  const [url, setUrl] = useState('');
  const [actif, setActif] = useState(true);
  const [secret, setSecret] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const [c, l] = await Promise.all([api.webhook(), api.livraisons()]);
      setConfig(c);
      setLivraisons(l.data);
      setUrl(c.data?.url ?? '');
      setActif(c.data?.enabled ?? true);
    } catch (e) {
      setErreur(e);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  async function enregistrer(regenerer: boolean) {
    setErreur(null);
    setSecret(null);
    setEnCours(true);
    try {
      const endpoint = await api.definirWebhook(url.trim(), actif, regenerer);
      if (endpoint.secret) setSecret(endpoint.secret);
      await charger();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  if (!config) return <Chargement quoi="Chargement des webhooks…" />;

  const calendrier = config.retry_schedule_seconds.map(formaterDelai).join(', ');

  return (
    <>
      <MessageErreur erreur={erreur} />

      {secret && (
        <MessageSucces>
          <div>
            Secret de signature. Copiez-le maintenant : il ne sera plus jamais affiché, et le
            régénérer invalidera vos vérifications en cours.
          </div>
          <code
            className="mono"
            style={{ display: 'block', marginTop: 8, userSelect: 'all', fontSize: 13 }}
          >
            {secret}
          </code>
        </MessageSucces>
      )}

      <Bandeau titre="Vérifiez la signature, et refusez un horodatage trop ancien.">
        L’en-tête <span className="mono">X-Infratex-Signature</span> vaut{' '}
        <span className="mono">t=&lt;epoch&gt;,v1=&lt;hex&gt;</span>, où{' '}
        <span className="mono">
          v1 = HMAC_SHA256(secret, &quot;&lt;t&gt;.&lt;corps brut&gt;&quot;)
        </span>
        . Comparez en temps constant <strong>et</strong> refusez au-delà de cinq minutes : sans ce
        second contrôle, la signature ne protège pas du rejeu. Un exemple complet figure dans{' '}
        <strong>Bac à sable → Exemples de code</strong>.
      </Bandeau>

      <div className="rangee">
        <div className="large">
          <Carte
            titre={`Point de terminaison — ${estProduction ? 'production' : 'plateforme de test'}`}
          >
            <label className="champ" style={{ marginBottom: 14 }}>
              <span>URL appelée</span>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://exemple.ga/webhooks/sms"
              />
              <div className="aide">
                Répondez en moins de {Math.round(config.timeout_ms / 1000)} secondes et traitez en
                différé : le délai d’attente compte comme un échec.
              </div>
            </label>

            <label
              className="champ"
              style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}
            >
              <input
                type="checkbox"
                checked={actif}
                onChange={(e) => setActif(e.target.checked)}
                style={{ width: 'auto' }}
              />
              <span style={{ margin: 0 }}>Livraisons activées</span>
            </label>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                className="primaire"
                onClick={() => enregistrer(false)}
                disabled={enCours || !url.trim()}
              >
                {enCours ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              {config.data && (
                <button
                  className="secondaire"
                  onClick={() => enregistrer(true)}
                  disabled={enCours || !url.trim()}
                >
                  Régénérer le secret
                </button>
              )}
            </div>

            {config.data && (
              <div className="aide">
                Configuré le {formaterDate(config.data.created_at)}, modifié{' '}
                {formaterDate(config.data.updated_at)}. Régénérer le secret est une action explicite
                : vos vérifications de signature échoueront jusqu’à ce que vous ayez déployé le
                nouveau.
              </div>
            )}
          </Carte>
        </div>

        <div className="etroit">
          <Carte titre="Relances">
            <dl className="definitions">
              <dt>Tentatives</dt>
              <dd>{config.max_attempts}</dd>
              <dt>Calendrier</dt>
              <dd style={{ fontVariantNumeric: 'normal' }}>{calendrier}</dd>
              <dt>Délai d’attente</dt>
              <dd>{config.timeout_ms} ms</dd>
            </dl>
            <div className="aide">
              Une réponse <span className="mono">2xx</span> clôt la livraison. Un{' '}
              <span className="mono">4xx</span> autre que 408 et 429 est un refus définitif : on
              n’insiste pas. Le calendrier couvre un peu plus de vingt-quatre heures — de quoi
              déployer un correctif avant l’abandon.
            </div>
          </Carte>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Événements émis">
          <div className="tableau-conteneur">
            <table style={{ minWidth: 520 }}>
              <thead>
                <tr>
                  <th>Événement</th>
                  <th>Signification</th>
                </tr>
              </thead>
              <tbody>
                {EVENEMENTS.map(([nom, sens]) => (
                  <tr key={nom}>
                    <td className="mono" style={{ fontSize: 12.5 }}>
                      {nom}
                    </td>
                    <td style={{ color: 'var(--muet)' }}>{sens}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Carte>
      </div>

      <div style={{ marginTop: 16 }}>
        <Carte
          titre="Journal des livraisons"
          action={
            <button className="secondaire petit" onClick={() => void charger()}>
              Actualiser
            </button>
          }
          sansCorps
        >
          {livraisons === null ? (
            <Chargement />
          ) : livraisons.length === 0 ? (
            <EtatVide titre="Aucune livraison pour l’instant.">
              <div className="aide" style={{ marginTop: 0 }}>
                Les événements sont publiés dès qu’un message atteint un état terminal. Envoyez un
                message une fois le point de terminaison configuré.
              </div>
            </EtatVide>
          ) : (
            <div className="tableau-conteneur">
              <table style={{ minWidth: 780 }}>
                <thead>
                  <tr>
                    <th>Événement</th>
                    <th>Message</th>
                    <th>État</th>
                    <th className="droite">Tentatives</th>
                    <th className="droite">Code HTTP</th>
                    <th>Dernière erreur</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {livraisons.map((l) => {
                    const etat = ETAT_LIVRAISON[l.status];
                    return (
                      <tr key={l.id}>
                        <td className="mono" style={{ fontSize: 12.5 }}>
                          {l.event_type}
                        </td>
                        <td className="mono" style={{ fontSize: 11.5, color: 'var(--muet)' }}>
                          {l.message_id ?? '—'}
                        </td>
                        <td>
                          <PuceLibre ton={etat.ton}>{etat.libelle}</PuceLibre>
                        </td>
                        <td className="nombre">{l.attempts}</td>
                        <td className="nombre">{l.last_status_code ?? '—'}</td>
                        <td
                          style={{
                            color: 'var(--rouge)',
                            fontSize: 12.5,
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={l.last_error ?? ''}
                        >
                          {l.last_error ?? '—'}
                        </td>
                        <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                          {formaterDate(l.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>
    </>
  );
}
